import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: API_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      // Only logout on auth-related 401s, not on data endpoints
      if (url.includes('/auth/') || url.includes('/auth/me')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (email, password) => {
    const formData = new FormData();
    formData.append('username', email);
    formData.append('password', password);
    return api.post('/api/auth/login', formData);
  },
  register: (data) => api.post('/api/auth/register', data),
  getMe: () => api.get('/api/auth/me'),
  changePassword: (currentPassword, newPassword) => api.put('/api/auth/change-password', { current_password: currentPassword, new_password: newPassword }),
  forgotPassword: (email) => api.post('/api/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/api/auth/reset-password', { token, new_password: newPassword }),
};

export const crewAPI = {
  getAll: (opts = {}) => {
    const params = {};
    if (opts.include_archived) params.include_archived = 1;
    if (opts.only_archived) params.only_archived = 1;
    return api.get('/api/crew-members', { params });
  },
  getOne: (id) => api.get(`/api/crew-members/${id}`),
  create: (data) => api.post('/api/crew-members', data),
  update: (id, data) => api.put(`/api/crew-members/${id}`, data),
  delete: (id, { hard = false } = {}) => api.delete(`/api/crew-members/${id}`, { params: hard ? { hard: 1 } : {} }),
  restore: (id) => api.post(`/api/crew-members/${id}/restore`),
  duplicates: () => api.get('/api/crew-members/duplicates'),
  merge: (data) => api.post('/api/crew-members/merge', data),
  uploadPhoto: (id, formData) => api.post(`/api/crew-members/${id}/photo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  uploadMyPhoto: (formData) => api.post('/api/crew/my-profile/photo', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

export const searchAPI = {
  query: (q) => api.get('/api/search', { params: { q } }),
};

export const gearAPI = {
  getAll: () => api.get('/api/gear'),
  create: (data) => api.post('/api/gear', data),
  update: (id, data) => api.put(`/api/gear/${id}`, data),
  delete: (id) => api.delete(`/api/gear/${id}`),
};

export const projectAPI = {
  getAll: (status) => api.get('/api/projects', { params: status ? { status } : {} }),
  getOne: (id) => api.get(`/api/projects/${id}`),
  create: (data) => api.post('/api/projects', data),
  update: (id, data) => api.put(`/api/projects/${id}`, data),
  delete: (id) => api.delete(`/api/projects/${id}`),
  archive: (id) => api.put(`/api/projects/${id}/archive`),
  unarchive: (id) => api.put(`/api/projects/${id}/unarchive`),
};

export const eventAPI = {
  getAll: () => api.get('/api/events'),
  getByProject: (projectId) => api.get(`/api/projects/${projectId}/events`),
  getOne: (id) => api.get(`/api/events/${id}`),
  create: (projectId, data) => api.post(`/api/projects/${projectId}/events`, data),
  update: (id, data) => api.put(`/api/events/${id}`, data),
  delete: (id) => api.delete(`/api/events/${id}`),
  getAssignments: (eventId) => api.get(`/api/events/${eventId}/assignments`),
};

export const assignmentAPI = {
  create: (data) => api.post('/api/assignments', data),
  getByEvent: (eventId) => api.get(`/api/events/${eventId}/assignments`),
  delete: (id) => api.delete(`/api/assignments/${id}`),
  updatePayment: (id, status) => api.put(`/api/assignments/${id}/payment`, { payment_status: status }),
  updateAmount: (id, data) => api.put(`/api/assignments/${id}/amount`, data),
  updateConfirmation: (id, status) => api.put(`/api/assignments/${id}/confirm`, { confirmation_status: status }),
  confirmGear: (id) => api.post(`/api/assignments/${id}/confirm-gear`),
  markWorked: (id) => api.post(`/api/assignments/${id}/mark-worked`),
  pendingConfirmations: () => api.get('/api/assignments/pending-confirmations'),
};

export const gearChecklistAPI = {
  sendNow: (eventId) => api.post(`/api/events/${eventId}/send-gear-checklist`),
  confirmPublic: (token) => api.get('/api/confirm-gear', { params: { token } }),
};

export const shotListAPI = {
  get: (eventId) => api.get(`/api/events/${eventId}/shot-list`),
  addItem: (eventId, data) => api.post(`/api/events/${eventId}/shot-list/items`, data),
  updateItem: (itemId, data) => api.put(`/api/shot-list-items/${itemId}`, data),
  deleteItem: (itemId) => api.delete(`/api/shot-list-items/${itemId}`),
  uploadReference: (eventId, formData) => api.post(`/api/events/${eventId}/shot-list/upload-reference`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  enablePublicLink: (eventId) => api.post(`/api/events/${eventId}/shot-list/public-link`),
  disablePublicLink: (eventId) => api.delete(`/api/events/${eventId}/shot-list/public-link`),
};

export const publicShotListAPI = {
  get: (token) => api.get(`/api/public/shot-list/${token}`),
  submit: (token, data) => api.post(`/api/public/shot-list/${token}/items`, data),
  uploadReference: (token, formData) => api.post(`/api/public/shot-list/${token}/upload-reference`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

export const paymentAPI = {
  getSummary: () => api.get('/api/payments/summary'),
};

export const analyticsAPI = {
  getDashboard: () => api.get('/api/analytics/dashboard'),
};

export const financeAPI = {
  getProject: (projectId) => api.get(`/api/projects/${projectId}/finance`),
  updateProject: (projectId, data) => api.put(`/api/projects/${projectId}/finance`, data),
  overview: () => api.get('/api/finance/overview'),
};

export const calendarAPI = {
  connect: () => api.post('/api/calendar/connect'),
};

export const blockedDatesAPI = {
  getAll: (crewMemberId) => api.get('/api/blocked-dates', { params: crewMemberId ? { crew_member_id: crewMemberId } : {} }),
  create: (data) => api.post('/api/blocked-dates', data),
  delete: (id) => api.delete(`/api/blocked-dates/${id}`),
};

export const personalGearAPI = {
  getAll: () => api.get('/api/crew/my-gear'),
  add: (name) => api.post('/api/crew/my-gear', { name }),
  toggle: (id) => api.put(`/api/crew/my-gear/${id}/toggle`),
  delete: (id) => api.delete(`/api/crew/my-gear/${id}`),
  reset: () => api.post('/api/crew/my-gear/reset'),
};

export const driveAPI = {
  getStatus: () => api.get('/api/oauth/drive/status'),
  connect: () => api.get('/api/oauth/drive/login'),
  disconnect: () => api.post('/api/oauth/drive/disconnect'),
  upload: (formData) => api.post('/api/drive/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  listFiles: (projectId, eventId) => api.get('/api/drive/files', { params: { project_id: projectId, event_id: eventId } }),
  deleteFile: (fileId) => api.delete(`/api/drive/files/${fileId}`),
};

export const notificationAPI = {
  send: (assignmentIds, notificationType, customMessage = null) => 
    api.post('/api/notifications/send', { 
      assignment_ids: assignmentIds, 
      notification_type: notificationType,
      custom_message: customMessage
    }),
  getConfig: () => api.get('/api/notifications/config'),
  sendReminder: (assignmentIds) =>
    api.post('/api/notifications/send-reminder', { assignment_ids: assignmentIds }),
};

export const notifCenterAPI = {
  list: () => api.get('/api/notifications'),
  unreadCount: () => api.get('/api/notifications/unread-count'),
  markRead: (id) => api.put(`/api/notifications/${id}/read`),
  markAllRead: () => api.post('/api/notifications/read-all'),
};

export const pushAPI = {
  register: (token, platform) => api.post('/api/push/register', { token, platform }),
  unregister: (token) => api.post('/api/push/unregister', { token }),
  test: () => api.post('/api/push/test'),
};

export const todoAPI = {
  list: () => api.get('/api/todos'),
  create: (text, due_date = null) => api.post('/api/todos', { text, due_date }),
  update: (id, data) => api.put(`/api/todos/${id}`, data),
  delete: (id) => api.delete(`/api/todos/${id}`),
  suggestions: () => api.get('/api/todos/suggestions'),
  acceptSuggestion: (suggestion_key, text) => api.post('/api/todos/suggestions/accept', { suggestion_key, text }),
  dismissSuggestion: (suggestion_key) => api.post('/api/todos/suggestions/dismiss', { suggestion_key }),
};

export const leadAPI = {
  list: () => api.get('/api/leads'),
  stats: () => api.get('/api/leads/stats'),
  create: (data) => api.post('/api/leads', data),
  update: (id, data) => api.put(`/api/leads/${id}`, data),
  delete: (id) => api.delete(`/api/leads/${id}`),
  convert: (id) => api.post(`/api/leads/${id}/convert`),
  submitPublic: (data) => api.post('/api/public/leads', data),
};

export const workflowAPI = {
  // Projects
  getProjects: () => api.get('/api/workflow/projects'),
  createProject: (data) => api.post('/api/workflow/projects', data),
  updateProject: (id, data) => api.put(`/api/workflow/projects/${id}`, data),
  deleteProject: (id) => api.delete(`/api/workflow/projects/${id}`),
  // Events
  getEvents: (projectId) => api.get('/api/workflow/events', { params: projectId ? { project_id: projectId } : {} }),
  createEvent: (data) => api.post('/api/workflow/events', data),
  updateEvent: (id, data) => api.put(`/api/workflow/events/${id}`, data),
  deleteEvent: (id) => api.delete(`/api/workflow/events/${id}`),
  // Crew Uploads
  getCrewUploads: (projectId, eventId) => api.get('/api/workflow/crew-uploads', { params: { ...(projectId && { project_id: projectId }), ...(eventId && { event_id: eventId }) } }),
  createCrewUpload: (data) => api.post('/api/workflow/crew-uploads', data),
  verifyUpload: (id) => api.put(`/api/workflow/crew-uploads/${id}/verify`),
  unverifyUpload: (id) => api.put(`/api/workflow/crew-uploads/${id}/unverify`),
  updateUploadNotes: (id, notes) => api.put(`/api/workflow/crew-uploads/${id}/notes`, { notes }),
  updateDeliveryMethod: (id, method) => api.put(`/api/workflow/crew-uploads/${id}/delivery-method`, { delivery_method: method }),
  // Dashboard
  getDashboardStats: () => api.get('/api/workflow/dashboard/stats'),
  // Sync
  syncFromScheduling: () => api.post('/api/workflow/sync'),
  // Albums
  getAlbums: (projectId) => api.get('/api/workflow/albums', { params: projectId ? { project_id: projectId } : {} }),
  createAlbum: (data) => api.post('/api/workflow/albums', data),
  updateAlbum: (id, data) => api.put(`/api/workflow/albums/${id}`, data),
  deleteAlbum: (id) => api.delete(`/api/workflow/albums/${id}`),
};

export default api;